import '../css/app.css';

import { createInertiaApp } from '@inertiajs/react';
import type { i18n as I18nType } from 'i18next';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { PostHogProvider } from 'posthog-js/react';
import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { DigestAudioPlayer } from './components/digest-audio-player';
import { Toaster } from './components/ui/sonner';
import { DigestPlayerProvider } from './contexts/digest-player-context';
import { initializeEcho } from './echo';
import { initializeTheme } from './hooks/use-appearance';
import i18nPromise from './i18n';
import type { SharedData } from './types';

const appName = 'Zero by Right Inbox';

const posthogOptions = {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    ui_host: 'https://eu.posthog.com',
};

const AppProviders = ({ i18n, children }: { i18n: I18nType; children: React.ReactNode }) => {
    return (
        <I18nextProvider i18n={i18n}>
            <DigestPlayerProvider>
                {children}
                <DigestAudioPlayer />
                <Toaster position="top-right" />
            </DigestPlayerProvider>
        </I18nextProvider>
    );
};

async function initializeApp() {
    const i18n = await i18nPromise;

    if (typeof window !== 'undefined') {
        initializeEcho();
    }

    createInertiaApp<SharedData>({
        title: (title) => `${title} - ${appName}`,
        resolve: (name) => {
            return resolvePageComponent(
                [
                    `./pages/${name}.tsx`,
                    `../../modules/Auth/Resources/js/pages/${name}.tsx`,
                    `../../modules/Billing/Resources/js/pages/${name}.tsx`,
                    `../../modules/Homepage/Resources/js/pages/${name}.tsx`,
                    `../../modules/Settings/Resources/js/pages/${name}.tsx`,
                    `../../modules/Onboarding/Resources/js/pages/${name}.tsx`,
                    `../../modules/Dashboard/Resources/js/pages/${name}.tsx`,
                    `../../modules/Workflows/Resources/js/pages/${name}.tsx`,
                    `../../modules/Digests/Resources/js/pages/${name}.tsx`,
                    `../../modules/Email/Resources/js/pages/${name}.tsx`,
                    `../../modules/Insights/Resources/js/pages/${name}.tsx`,
                    `../../modules/VoiceAgent/Resources/js/pages/${name}.tsx`,
                ],
                {
                    ...import.meta.glob('./pages/**/*.tsx'),
                    ...import.meta.glob('../../modules/Auth/Resources/js/pages/**/*.tsx'),
                    ...import.meta.glob('../../modules/Billing/Resources/js/pages/**/*.tsx'),
                    ...import.meta.glob('../../modules/Homepage/Resources/js/pages/**/*.tsx'),
                    ...import.meta.glob('../../modules/Settings/Resources/js/pages/**/*.tsx'),
                    ...import.meta.glob('../../modules/Onboarding/Resources/js/pages/**/*.tsx'),
                    ...import.meta.glob('../../modules/Dashboard/Resources/js/pages/**/*.tsx'),
                    ...import.meta.glob('../../modules/Workflows/Resources/js/pages/**/*.tsx'),
                    ...import.meta.glob('../../modules/Digests/Resources/js/pages/**/*.tsx'),
                    ...import.meta.glob('../../modules/Email/Resources/js/pages/**/*.tsx'),
                    ...import.meta.glob('../../modules/Insights/Resources/js/pages/**/*.tsx'),
                    ...import.meta.glob('../../modules/VoiceAgent/Resources/js/pages/**/*.tsx'),
                },
            );
        },
        setup({ el, App, props }) {
            const userLanguage = props.initialPage.props.auth?.user?.language;
            if (userLanguage && userLanguage !== i18n.language) {
                i18n.changeLanguage(userLanguage);
            }

            const root = createRoot(el);
            root.render(
                <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={posthogOptions}>
                    <AppProviders i18n={i18n}>
                        <Suspense fallback={null}>
                            <App {...props} />
                        </Suspense>
                    </AppProviders>
                </PostHogProvider>,
            );
        },
        progress: {
            color: '#80b340',
        },
    });
}

initializeApp();
initializeTheme();
